import React, { useEffect, useMemo, useState } from "react";
import {
  X,
  Loader2,
  UserPlus,
  Save,
  User,
  Phone,
  Mail,
  Building2,
  ShieldCheck,
  DollarSign,
  Briefcase,
  MapPin,
  Wallet,
  Lock,
  FileText,
  ShieldAlert,
  BookOpen,
  Info,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { toTitleCaseVietnamese } from "../../../utils/textUtils";
import {
  createEmployee,
  getEmployeeById,
  updateEmployee,
  getNextEmployeeCode,
  getEmployeeContracts,
  createEmployeeContract,
  updateEmployeeContract,
} from "../../../api/hrm/employees/employeeInfo";
import {
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { hasEmployeeSensitivePermission, hasPermission } from "../../../utils/permissionUtils";
import { getDepartmentsByBranch } from "../../../api/hrm/departments/departmentInfo";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import { getPositionsByBranch } from "../../../api/hrm/positions/positionInfo";
import { getUsageRoleOptions } from "../../../api/settings/usageRoles";
import {
  assignUserAccess,
  getUserAccessSummary,
} from "../../../api/settings/userAccess";
import EmployeeDocumentAccessPanel from "./EmployeeDocumentAccessPanel";
import EmployeeHrDocumentsPanel from "./EmployeeHrDocumentsPanel";
import { VIETNAM_BANKS } from "../../../data/vietnamBanks";
import { modalSelectPortalProps } from "../../../components/ui/customerSelectInModal";

/* ================= COMPONENT PHỤ: SECTION ================= */
const Section = ({ icon: Icon, title, subTitle, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm space-y-4">
    <div className="flex items-center gap-3 pb-2 border-b border-gray-50">
      <div className="p-2 bg-red-50 rounded-lg">
        <Icon className="w-5 h-5 text-red-600" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-tight">
          {title}
        </h3>
        {subTitle && <p className="text-xs text-gray-400">{subTitle}</p>}
      </div>
    </div>
    <div className="pt-2">{children}</div>
  </div>
);

/* ================= COMPONENT PHỤ: FLOAT LABEL ================= */
const FloatLabel = ({ label, required, children }) => (
  <div className="relative">
    <span className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </span>
    {children}
  </div>
);

const INPUT_CLASS =
  "w-full border rounded-xl px-4 py-3 border-gray-200 outline-none focus:ring-2 focus:ring-red-100 focus:border-red-400 transition-all text-sm text-gray-800 font-medium";

const CLASSIFICATION_OPTIONS = [
  { value: "nhan_vien_chinh_thuc", label: "Nhân viên chính thức" },
  { value: "thuc_tap_sinh", label: "Thực tập sinh" },
  { value: "nhan_vien_thoi_vu", label: "Nhân viên thời vụ" },
  { value: "gv_thinh_giang", label: "Giáo viên thỉnh giảng" },
  { value: "gv_thoi_vu", label: "Giáo viên thời vụ" },
  { value: "gv_chinh_thuc", label: "Giáo viên chính thức" },
];

const CONTRACT_TYPE_OPTIONS = [
  { value: "indefinite", label: "Hợp đồng không xác định thời hạn" },
  { value: "fixed_12", label: "Hợp đồng 12 tháng" },
  { value: "fixed_24", label: "Hợp đồng 24 tháng" },
  { value: "seasonal", label: "Hợp đồng thời vụ / dưới 12 tháng" },
  { value: "probation", label: "Hợp đồng thử việc" },
  { value: "internship", label: "Hợp đồng thực tập" },
  { value: "collaborator", label: "Hợp đồng cộng tác viên" },
  { value: "service", label: "Hợp đồng dịch vụ" },
];

const INITIAL_FORM_DATA = {
  code: "",
  name: "",
  email: "",
  phone: "",
  birthday: "",
  gender: "",
  department_id: "",
  position: "",
  branch_id: "",
  remark: "",
  address: "",
  personal_id: "",
  personal_email: "",
  annual_leave_total: 12,
  is_active: true,
  avatar_url: "",
  employment_classification: "nhan_vien_chinh_thuc",
  is_teacher: false,
  payroll_bank_code: "",
  payroll_bank_account_number: "",
  payroll_bank_account_name: "",
  insurance_salary: "",
  tax_dependent_count: 0,
  insurance_enrolled: true,
  pit_resident: true,
};

const INITIAL_CONTRACT_FORM = {
  contract_no: "",
  contract_type: "indefinite",
  start_date: "",
  end_date: "",
  position: "",
  salary: "",
  allowance: "",
  note: "",
  is_active: true,
};

const LockedPanel = ({ title, hint }) => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-amber-200 bg-amber-50/80 px-6 py-14 text-center">
    <div className="rounded-2xl bg-amber-100 p-3 text-amber-800">
      <Lock className="h-6 w-6" strokeWidth={2.2} />
    </div>
    <p className="text-sm font-black uppercase tracking-wide text-amber-950">{title}</p>
    <p className="max-w-md text-xs leading-relaxed text-amber-900/80">{hint}</p>
  </div>
);

const UsageHint = ({ children, tone = "blue" }) => {
  const tones = {
    blue: "border-blue-100 bg-blue-50 text-blue-900",
    slate: "border-slate-200 bg-slate-50 text-slate-700",
  };
  return (
    <div
      className={`mb-4 flex items-start gap-2.5 rounded-xl border px-3.5 py-3 text-xs leading-relaxed ${tones[tone] || tones.blue}`}
    >
      <Info className="mt-0.5 h-4 w-4 shrink-0 opacity-80" strokeWidth={2.2} />
      <div className="space-y-1">{children}</div>
    </div>
  );
};

const EmployeeFormModal = ({ isOpen, onClose, employeeId = null }) => {
  const [isSaving, setIsSaving] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [branches, setBranches] = useState([]);
  const [positions, setPositions] = useState([]);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [activeTab, setActiveTab] = useState("profile");

  const canSensitiveEdit = hasEmployeeSensitivePermission("U");
  const canSensitiveView =
    hasEmployeeSensitivePermission("R") || canSensitiveEdit;
  /** Giữ alias dùng trong logic hợp đồng / lưu */
  const canSensitive = canSensitiveEdit;

  const [contracts, setContracts] = useState([]);
  const [selectedContractId, setSelectedContractId] = useState(null);
  const [contractForm, setContractForm] = useState(INITIAL_CONTRACT_FORM);
  const [usageRoleOptions, setUsageRoleOptions] = useState([]);
  const [usageRoleId, setUsageRoleId] = useState("");

  const canAssignUsageRole =
    hasPermission("settings.user_roles", "U") ||
    hasPermission("settings.usage_roles", "C");

  // 🧩 Load dữ liệu khi mở form
  useEffect(() => {
    if (!isOpen) return;
    setActiveTab("profile");
    const loadInitialData = async () => {
      try {
        const branchRes = await getAllBranches_Array();
        setBranches((Array.isArray(branchRes) ? branchRes : branchRes?.data || []).map((b) => ({
          value: b.id,
          label: b.name,
        })));

        // Bộ phận + chức vụ sẽ được load theo chi nhánh ở useEffect bên dưới
        setDepartments([]);
        setPositions([]);

        if (employeeId) {
          const emp = await getEmployeeById(employeeId);
          setFormData({
            ...INITIAL_FORM_DATA,
            ...emp,
            birthday: emp.birthday || emp.birthdate || "",
            position: emp.position || "",
            employment_classification:
              emp.employment_classification || "nhan_vien_chinh_thuc",
            is_teacher: !!emp.is_teacher,
            payroll_bank_code: emp.payroll_bank_code || "",
            payroll_bank_account_number: emp.payroll_bank_account_number || "",
            payroll_bank_account_name: emp.payroll_bank_account_name || "",
            insurance_salary:
              emp.insurance_salary != null && emp.insurance_salary !== ""
                ? String(emp.insurance_salary)
                : "",
            tax_dependent_count: Number(emp.tax_dependent_count) || 0,
            insurance_enrolled: emp.insurance_enrolled !== false,
            pit_resident: emp.pit_resident !== false,
          });
          setAvatarPreview(emp.avatar_url);
          if (canAssignUsageRole) {
            try {
              const access = await getUserAccessSummary(employeeId);
              setUsageRoleId(access?.primaryRole?.usage_role_id || "");
            } catch {
              setUsageRoleId("");
            }
          }
        } else {
          const code = await getNextEmployeeCode();
          setFormData({
            ...INITIAL_FORM_DATA,
            code,
          });
          setAvatarPreview(null);
          setContracts([]);
          setSelectedContractId(null);
          setContractForm(INITIAL_CONTRACT_FORM);
          setUsageRoleId("");
        }
      } catch (err) {
        console.error("Lỗi khi load dữ liệu:", err);
      }
    };
    loadInitialData();
  }, [isOpen, employeeId, canAssignUsageRole]);

  // Quyền sử dụng — lọc theo CN/BP
  useEffect(() => {
    if (!isOpen || !canAssignUsageRole) {
      setUsageRoleOptions([]);
      return;
    }
    const params = {};
    if (formData.branch_id) params.branch_id = formData.branch_id;
    if (formData.department_id) params.department_id = formData.department_id;
    getUsageRoleOptions(params)
      .then((rows) =>
        setUsageRoleOptions(
          (rows || []).map((r) => ({
            value: r.id,
            label: `${r.label}${r.department_name ? ` (${r.department_name})` : r.branch_name ? ` (${r.branch_name})` : ""}`,
          }))
        )
      )
      .catch(() => setUsageRoleOptions([]));
  }, [isOpen, canAssignUsageRole, formData.branch_id, formData.department_id]);

  // 🧩 Load bộ phận + chức vụ theo chi nhánh
  useEffect(() => {
    if (!isOpen) return;

    const branchId = formData.branch_id;
    if (!branchId) {
      setDepartments([]);
      setPositions([]);
      setFormData((prev) => ({
        ...prev,
        department_id: "",
        position: "",
      }));
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const [deptRes, posRes] = await Promise.all([
          getDepartmentsByBranch(branchId),
          getPositionsByBranch(branchId),
        ]);

        const deptList = Array.isArray(deptRes)
          ? deptRes
          : Array.isArray(deptRes?.data)
            ? deptRes.data
            : Array.isArray(deptRes?.items)
              ? deptRes.items
              : [];

        const posList = Array.isArray(posRes)
          ? posRes
          : Array.isArray(posRes?.data)
            ? posRes.data
            : Array.isArray(posRes?.items)
              ? posRes.items
              : [];

        const deptOptions = deptList.map((d) => ({ value: d.id, label: d.name }));
        const posOptions = posList.map((p) => ({ value: p.id, label: p.name }));

        if (cancelled) return;

        setDepartments(deptOptions);
        setPositions(posOptions);

        // employeeInfo có thể trả về position là "name" (nếu join system.roles thành công)
        // -> normalize về id để react-select hiển thị đúng.
        setFormData((prev) => {
          const nextDepartmentId = deptOptions.some((d) => d.value === prev.department_id)
            ? prev.department_id
            : "";

          const nextPosition =
            posOptions.some((p) => p.value === prev.position)
              ? prev.position
              : posOptions.find((p) => p.label === prev.position)?.value || "";

          return {
            ...prev,
            department_id: nextDepartmentId,
            position: nextPosition,
          };
        });
      } catch (err) {
        console.error("Lỗi load bộ phận/chức vụ theo chi nhánh:", err);
        if (cancelled) return;
        setDepartments([]);
        setPositions([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, formData.branch_id]);

  // Load hợp đồng hiện có
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isOpen || !employeeId || !canSensitive) return;
      try {
        const list = await getEmployeeContracts(employeeId);
        if (cancelled) return;
        const safeList = Array.isArray(list) ? list : [];
        setContracts(safeList);
        const first = safeList[0] || null;
        if (first) {
          setSelectedContractId(first.id);
          setContractForm({
            contract_no: first.contract_no || "",
            contract_type: first.contract_type || "indefinite",
            start_date: first.start_date || "",
            end_date: first.end_date || "",
            position: first.position || "",
            salary: first.salary != null ? String(first.salary) : "",
            allowance: first.allowance != null ? String(first.allowance) : "",
            note: first.note || "",
            is_active: first.is_active ?? true,
          });
        } else {
          setSelectedContractId(null);
          setContractForm(INITIAL_CONTRACT_FORM);
        }
      } catch (e) {
        console.error("Load contracts failed:", e);
        setSelectedContractId(null);
        setContractForm(INITIAL_CONTRACT_FORM);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, employeeId, canSensitive]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSelectChange = (field, option) => {
    setFormData((prev) => ({ ...prev, [field]: option ? option.value : "" }));
  };

  const handleAvatarSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        setAvatarPreview(evt.target.result);
        setFormData((prev) => ({ ...prev, avatar_url: evt.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleContractChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContractForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    const missingBase = !formData.name || !formData.email || !formData.phone;
    const missingPersonalEmail = !employeeId && !formData.personal_email;

    if (missingBase || missingPersonalEmail) {
      showErrorToast(
        missingPersonalEmail
          ? "Vui lòng nhập Email cá nhân"
          : "Vui lòng nhập đầy đủ Họ tên, Email và Số điện thoại"
      );
      return;
    }
    setIsSaving(true);
    try {
      const birthdate = formData.birthday || formData.birthdate;
      let payload = {
        ...formData,
        birthdate,
        department_id: formData.department_id || null,
        payroll_bank_code: formData.payroll_bank_code?.trim() || null,
        payroll_bank_account_number: formData.payroll_bank_account_number?.trim() || null,
        payroll_bank_account_name: formData.payroll_bank_account_name?.trim() || null,
        insurance_salary:
          formData.insurance_salary === "" || formData.insurance_salary == null
            ? null
            : Number(formData.insurance_salary),
        tax_dependent_count: Math.max(0, parseInt(formData.tax_dependent_count, 10) || 0),
        insurance_enrolled: formData.insurance_enrolled !== false,
        pit_resident: formData.pit_resident !== false,
      };
      delete payload.birthday;
      if (!employeeId || !canSensitive) {
        delete payload.annual_leave_total;
        delete payload.personal_id;
        delete payload.remark;
        delete payload.address;
      }
      if (!canSensitive) {
        delete payload.payroll_bank_code;
        delete payload.payroll_bank_account_number;
        delete payload.payroll_bank_account_name;
        delete payload.insurance_salary;
        delete payload.tax_dependent_count;
        delete payload.insurance_enrolled;
        delete payload.pit_resident;
      }

      if (employeeId) {
        await updateEmployee(employeeId, payload);
        if (canSensitive) {
          const hasContractInput =
            selectedContractId ||
            contractForm.contract_no ||
            contractForm.start_date ||
            contractForm.end_date ||
            contractForm.position ||
            contractForm.salary !== "" ||
            contractForm.allowance !== "" ||
            contractForm.note;

          if (hasContractInput) {
            const salaryNum = contractForm.salary === "" ? null : Number(contractForm.salary);
            const allowanceNum = contractForm.allowance === "" ? null : Number(contractForm.allowance);

            const contractPayloadBase = {
              contract_no: contractForm.contract_no || null,
              contract_type: contractForm.contract_type || null,
              start_date: contractForm.start_date || null,
              end_date: contractForm.end_date || null,
              position: contractForm.position || null,
              salary: Number.isNaN(salaryNum) ? null : salaryNum,
              allowance: Number.isNaN(allowanceNum) ? null : allowanceNum,
              note: contractForm.note || null,
              is_active: contractForm.is_active ?? true,
            };

            if (selectedContractId) {
              await updateEmployeeContract(selectedContractId, contractPayloadBase);
            } else {
              await createEmployeeContract({ employee_id: employeeId, ...contractPayloadBase });
            }
          }
        }
        showSuccessToast("Cập nhật nhân viên thành công");
        if (canAssignUsageRole && usageRoleId) {
          await assignUserAccess(employeeId, { usage_role_id: usageRoleId });
        }
      } else {
        const created = await createEmployee(payload);
        if (canAssignUsageRole && usageRoleId && created?.id) {
          await assignUserAccess(created.id, { usage_role_id: usageRoleId });
        }
        showSuccessToast("Tạo nhân viên thành công");
      }
      onClose(true);
    } catch (error) {
      // Hiển thị chi tiết lỗi backend (vd: "Email đã tồn tại" / "Chức vụ không hợp lệ")
      const serverMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.response?.data?.error_description ||
        error?.message;

      console.error("Lỗi khi lưu nhân viên:", error);
      showErrorToast(serverMessage || "Không thể lưu nhân viên. Vui lòng thử lại.");
    } finally {
      setIsSaving(false);
    }
  };

  const formTabs = useMemo(() => {
    const tabs = [
      { id: "profile", label: "Hồ sơ", shortLabel: "Hồ sơ", icon: User, locked: false },
      {
        id: "sensitive",
        label: "Dữ liệu nhạy cảm",
        shortLabel: "Nhạy cảm",
        icon: ShieldCheck,
        locked: !canSensitiveView && !!employeeId,
      },
      {
        id: "payroll",
        label: "Lương & BHXH",
        shortLabel: "Lương",
        icon: Wallet,
        locked: !canSensitiveView,
      },
    ];
    if (employeeId && canSensitiveView) {
      tabs.push({
        id: "contract",
        label: "Hợp đồng",
        shortLabel: "Hợp đồng",
        icon: Briefcase,
        locked: !canSensitiveEdit,
      });
    }
    if (employeeId) {
      tabs.push({
        id: "elearning",
        label: "Elearning",
        shortLabel: "Elearning",
        icon: BookOpen,
        locked: false,
      });
    }
    return tabs;
  }, [employeeId, canSensitiveView, canSensitiveEdit]);

  useEffect(() => {
    if (!formTabs.some((t) => t.id === activeTab)) {
      setActiveTab("profile");
    }
  }, [formTabs, activeTab]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-gray-900/60 backdrop-blur-[2px]"
            onClick={() => onClose(false)}
          />
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="bg-slate-50 rounded-3xl shadow-2xl w-full max-w-5xl h-[min(860px,90vh)] flex flex-col overflow-hidden pointer-events-auto border border-white">
              
              {/* HEADER */}
              <div className="shrink-0 bg-white px-8 py-5 flex justify-between items-center border-b border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-200">
                    <UserPlus className="text-white w-7 h-7" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 leading-none">
                      {employeeId ? "Cập nhật hồ sơ nhân viên" : "Thêm nhân viên mới"}
                    </h2>
                    <p className="text-sm text-gray-400 mt-1">
                      {employeeId
                        ? "Hồ sơ theo tab — dữ liệu nhạy cảm chỉ hiện khi có quyền hrm.employees_sensitive."
                        : "Tạo hồ sơ cơ bản; CCCD, lương, BHXH nằm ở tab riêng theo phân quyền."}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onClose(false)}
                  className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400"
                >
                  <X size={24} />
                </button>
              </div>

              {/* TABS */}
              <div className="shrink-0 bg-white px-4 sm:px-8 border-b border-gray-100">
                <div className="flex gap-1 overflow-x-auto custom-scrollbar -mb-px">
                  {formTabs.map((tab) => {
                    const Icon = tab.icon;
                    const active = activeTab === tab.id;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-xs font-bold uppercase tracking-wide transition-colors ${
                          active
                            ? "border-red-600 text-red-700"
                            : "border-transparent text-gray-400 hover:text-gray-700"
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="hidden sm:inline">{tab.label}</span>
                        <span className="sm:hidden">{tab.shortLabel}</span>
                        {tab.locked ? <Lock className="h-3.5 w-3.5 text-amber-600" /> : null}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* BODY — chiều cao cố định; tab ngắn/dài không làm co giãn form */}
              <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar p-6 sm:p-8">
                {activeTab === "profile" && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Section icon={User} title="Nhân sự" subTitle="Dữ liệu định danh cá nhân">
                      <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                        <div className="relative group">
                          <img
                            src={avatarPreview || "https://cdn-icons-png.flaticon.com/512/149/149071.png"}
                            alt="avatar"
                            className="w-20 h-20 rounded-2xl object-cover ring-4 ring-white shadow-md"
                          />
                          <label className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 group-hover:opacity-100 rounded-2xl cursor-pointer transition-all text-[10px] font-bold">
                            THAY ĐỔI
                            <input type="file" accept="image/*" className="hidden" onChange={handleAvatarSelect} />
                          </label>
                        </div>
                        <div className="flex-1 space-y-3">
                          <FloatLabel label="Mã nhân viên" required>
                            <input
                              name="code"
                              value={formData.code}
                              readOnly
                              className="w-full border rounded-xl px-4 py-2 bg-white/50 border-gray-200 outline-none text-gray-500 font-bold"
                            />
                          </FloatLabel>
                          <label className="flex items-center gap-2 cursor-pointer group">
                            <input
                              type="checkbox"
                              name="is_teacher"
                              checked={!!formData.is_teacher}
                              onChange={handleChange}
                              className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-4 h-4"
                            />
                            <span className="text-xs font-bold text-gray-600 group-hover:text-red-600 transition-colors uppercase tracking-tight">
                              Là giáo viên / giảng viên
                            </span>
                          </label>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <FloatLabel label="Họ và tên" required>
                          <input
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            onBlur={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                name: toTitleCaseVietnamese(e.target.value),
                              }))
                            }
                            placeholder="NGUYỄN VĂN A"
                            className={`${INPUT_CLASS} font-black text-base`}
                          />
                        </FloatLabel>

                        <div className="grid grid-cols-2 gap-3">
                          <FloatLabel label="Ngày sinh">
                            <input
                              name="birthday"
                              value={formData.birthday || ""}
                              onChange={handleChange}
                              type="date"
                              className={INPUT_CLASS}
                            />
                          </FloatLabel>
                          <FloatLabel label="Giới tính">
                            <select
                              name="gender"
                              value={formData.gender || ""}
                              onChange={handleChange}
                              className={`${INPUT_CLASS} bg-white`}
                            >
                              <option value="">— Chọn —</option>
                              <option value="male">Nam</option>
                              <option value="female">Nữ</option>
                            </select>
                          </FloatLabel>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <FloatLabel label="Email công việc" required>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                type="email"
                                placeholder="ten@imavietnam.com"
                                className={`${INPUT_CLASS} pl-10`}
                              />
                            </div>
                          </FloatLabel>
                          <FloatLabel label="Số điện thoại" required>
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                type="tel"
                                placeholder="09xx..."
                                className={`${INPUT_CLASS} pl-10`}
                              />
                            </div>
                          </FloatLabel>
                        </div>
                      </div>
                    </Section>

                    <Section icon={Building2} title="Tổ chức" subTitle="Vị trí và phân loại nhân sự">
                      <div className="space-y-4">
                        <FloatLabel label="Chi nhánh" required>
                          <Select
                            styles={customSelectStyles}
                            options={branches}
                            value={branches.find((b) => b.value === formData.branch_id)}
                            onChange={(opt) => handleSelectChange("branch_id", opt)}
                            placeholder="Chọn chi nhánh..."
                          />
                        </FloatLabel>

                        <FloatLabel label="Bộ phận" required>
                          <Select
                            styles={customSelectStyles}
                            options={departments}
                            value={departments.find((d) => d.value === formData.department_id)}
                            onChange={(opt) => handleSelectChange("department_id", opt)}
                            placeholder="Chọn bộ phận..."
                          />
                        </FloatLabel>

                        <FloatLabel label="Chức vụ">
                          <Select
                            styles={customSelectStyles}
                            options={positions}
                            value={positions.find((item) => item.value === formData.position) || null}
                            onChange={(opt) => handleSelectChange("position", opt)}
                            placeholder="Chọn chức vụ..."
                            isClearable
                          />
                        </FloatLabel>

                        {canAssignUsageRole && (
                          <FloatLabel label="Quyền sử dụng">
                            <Select
                              styles={customSelectStyles}
                              options={usageRoleOptions}
                              value={
                                usageRoleOptions.find((o) => o.value === usageRoleId) || null
                              }
                              onChange={(opt) => setUsageRoleId(opt?.value || "")}
                              placeholder={
                                formData.branch_id
                                  ? "Chọn quyền theo CN/BP..."
                                  : "Chọn chi nhánh trước"
                              }
                              isClearable
                              isDisabled={!formData.branch_id}
                            />
                          </FloatLabel>
                        )}

                        <FloatLabel label="Phân loại nhân sự">
                          <Select
                            styles={customSelectStyles}
                            options={CLASSIFICATION_OPTIONS}
                            value={CLASSIFICATION_OPTIONS.find(
                              (o) => o.value === formData.employment_classification
                            )}
                            onChange={(opt) => handleSelectChange("employment_classification", opt)}
                          />
                        </FloatLabel>
                      </div>
                    </Section>
                  </div>
                )}

                {activeTab === "sensitive" && (
                  <>
                    {employeeId && !canSensitiveView ? (
                      <LockedPanel
                        title="Không có quyền xem dữ liệu nhạy cảm"
                        hint="Cần quyền hrm.employees_sensitive (R/U) để xem CCCD, địa chỉ, ngày phép và ghi chú nội bộ."
                      />
                    ) : (
                      <div className="mx-auto max-w-3xl space-y-4">
                        {!canSensitiveEdit ? (
                          <div className="flex items-start gap-3 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                            <p>
                              {canSensitiveView
                                ? "Bạn chỉ có quyền xem — không thể chỉnh sửa các trường nhạy cảm."
                                : "Khi tạo mới chỉ cần Email cá nhân. CCCD, địa chỉ, ngày phép cần quyền hrm.employees_sensitive (U)."}
                            </p>
                          </div>
                        ) : null}

                        <Section
                          icon={ShieldCheck}
                          title="Dữ liệu nhạy cảm"
                          subTitle="CCCD, phép, địa chỉ — chỉ HCNS / kế toán HQ"
                        >
                          <div className="space-y-4">
                            {(canSensitiveEdit || canSensitiveView) && (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <FloatLabel label="CMND / CCCD">
                                  <input
                                    name="personal_id"
                                    value={formData.personal_id || ""}
                                    onChange={handleChange}
                                    readOnly={!canSensitiveEdit}
                                    className={`${INPUT_CLASS} ${!canSensitiveEdit ? "bg-gray-50" : ""}`}
                                  />
                                </FloatLabel>
                                <FloatLabel label="Ngày phép / năm">
                                  <input
                                    name="annual_leave_total"
                                    type="number"
                                    value={formData.annual_leave_total ?? 12}
                                    onChange={handleChange}
                                    readOnly={!canSensitiveEdit}
                                    className={`${INPUT_CLASS} ${!canSensitiveEdit ? "bg-gray-50" : ""}`}
                                  />
                                </FloatLabel>
                              </div>
                            )}

                            <FloatLabel label="Email cá nhân" required={!employeeId}>
                              <input
                                name="personal_email"
                                type="email"
                                value={formData.personal_email || ""}
                                onChange={handleChange}
                                readOnly={!!employeeId && !canSensitiveEdit}
                                className={`${INPUT_CLASS} ${employeeId && !canSensitiveEdit ? "bg-gray-50" : ""}`}
                              />
                            </FloatLabel>

                            {(canSensitiveEdit || canSensitiveView) && (
                              <>
                                <FloatLabel label="Địa chỉ thường trú">
                                  <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                    <input
                                      name="address"
                                      value={formData.address || ""}
                                      onChange={handleChange}
                                      readOnly={!canSensitiveEdit}
                                      className={`${INPUT_CLASS} pl-10 ${!canSensitiveEdit ? "bg-gray-50" : ""}`}
                                    />
                                  </div>
                                </FloatLabel>
                                <FloatLabel label="Ghi chú nội bộ">
                                  <textarea
                                    name="remark"
                                    value={formData.remark || ""}
                                    onChange={handleChange}
                                    rows={2}
                                    readOnly={!canSensitiveEdit}
                                    className={`${INPUT_CLASS} resize-none min-h-[80px] ${!canSensitiveEdit ? "bg-gray-50" : ""}`}
                                  />
                                </FloatLabel>
                              </>
                            )}
                          </div>
                        </Section>
                      </div>
                    )}
                  </>
                )}

                {activeTab === "payroll" && (
                  <>
                    {!canSensitiveView ? (
                      <LockedPanel
                        title="Không có quyền xem lương & BHXH"
                        hint="Ngân hàng nhận lương, mức đóng BHXH và thuế TNCN thuộc dữ liệu nhạy cảm (hrm.employees_sensitive)."
                      />
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {!canSensitiveEdit ? (
                          <div className="md:col-span-2 flex items-start gap-3 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
                            <p>Chế độ chỉ xem — không lưu được thay đổi lương / BHXH / thuế.</p>
                          </div>
                        ) : null}

                        <Section
                          icon={Wallet}
                          title="Tài khoản nhận lương"
                          subTitle="Ngân hàng Việt Nam — chi trả lương"
                        >
                          <div className="space-y-4">
                            <FloatLabel label="Ngân hàng">
                              <Select
                                styles={customSelectStyles}
                                options={VIETNAM_BANKS}
                                value={
                                  VIETNAM_BANKS.find((b) => b.value === formData.payroll_bank_code) ||
                                  null
                                }
                                onChange={(opt) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    payroll_bank_code: opt?.value || "",
                                  }))
                                }
                                placeholder="— Chọn ngân hàng —"
                                isClearable
                                isDisabled={!canSensitiveEdit}
                                {...modalSelectPortalProps}
                              />
                            </FloatLabel>
                            <FloatLabel label="Số tài khoản">
                              <input
                                name="payroll_bank_account_number"
                                value={formData.payroll_bank_account_number || ""}
                                onChange={handleChange}
                                placeholder="Số tài khoản"
                                autoComplete="off"
                                readOnly={!canSensitiveEdit}
                                className={`${INPUT_CLASS} ${!canSensitiveEdit ? "bg-gray-50" : ""}`}
                              />
                            </FloatLabel>
                            <FloatLabel label="Tên tài khoản">
                              <input
                                name="payroll_bank_account_name"
                                value={formData.payroll_bank_account_name || ""}
                                onChange={handleChange}
                                onBlur={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    payroll_bank_account_name: toTitleCaseVietnamese(e.target.value),
                                  }))
                                }
                                placeholder="Chủ tài khoản"
                                readOnly={!canSensitiveEdit}
                                className={`${INPUT_CLASS} ${!canSensitiveEdit ? "bg-gray-50" : ""}`}
                              />
                            </FloatLabel>
                          </div>
                        </Section>

                        <Section
                          icon={DollarSign}
                          title="BHXH & Thuế TNCN"
                          subTitle="Dùng khi tính lương — mức đóng và giảm trừ"
                        >
                          <div className="space-y-4">
                            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                              <input
                                type="checkbox"
                                checked={formData.insurance_enrolled !== false}
                                disabled={!canSensitiveEdit}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    insurance_enrolled: e.target.checked,
                                  }))
                                }
                                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                              />
                              Tham gia BHXH / BHYT / BHTN
                            </label>
                            <FloatLabel label="Mức lương đóng BHXH (để trống = lương HĐ)">
                              <input
                                name="insurance_salary"
                                type="number"
                                min="0"
                                step="1000"
                                value={formData.insurance_salary || ""}
                                onChange={handleChange}
                                placeholder="VD: 10000000"
                                className={`${INPUT_CLASS} ${!canSensitiveEdit ? "bg-gray-50" : ""}`}
                                disabled={!canSensitiveEdit || formData.insurance_enrolled === false}
                              />
                            </FloatLabel>
                            <FloatLabel label="Số người phụ thuộc (thuế TNCN)">
                              <input
                                name="tax_dependent_count"
                                type="number"
                                min="0"
                                step="1"
                                value={formData.tax_dependent_count ?? 0}
                                onChange={handleChange}
                                readOnly={!canSensitiveEdit}
                                className={`${INPUT_CLASS} ${!canSensitiveEdit ? "bg-gray-50" : ""}`}
                              />
                            </FloatLabel>
                            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                              <input
                                type="checkbox"
                                checked={formData.pit_resident !== false}
                                disabled={!canSensitiveEdit}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    pit_resident: e.target.checked,
                                  }))
                                }
                                className="h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                              />
                              Cư trú thuế TNCN (biểu lũy tiến; bỏ chọn = 20% không cư trú)
                            </label>
                            <p className="text-[11px] leading-relaxed text-gray-500">
                              Mặc định: BHXH 8% + BHYT 1.5% + BHTN 1%; giảm trừ bản thân 11 triệu/tháng, người
                              phụ thuộc 4.4 triệu/người.
                            </p>
                          </div>
                        </Section>
                      </div>
                    )}
                  </>
                )}

                {activeTab === "contract" && (
                  <>
                    {!canSensitiveView ? (
                      <LockedPanel
                        title="Không có quyền xem hồ sơ hợp đồng"
                        hint="Cần quyền hrm.employees_sensitive (R/U) để xem và tải tài liệu nhân sự."
                      />
                    ) : (
                      <div className="mx-auto max-w-3xl space-y-6">
                        <UsageHint tone="slate">
                          <p className="font-semibold">Cách dùng tab Hợp đồng</p>
                          <ol className="list-decimal pl-4 space-y-0.5">
                            <li>
                              <b>Tài liệu nhân sự</b>: tải file lên để lưu trữ hồ sơ (có thể nhiều file HĐLĐ,
                              hồ sơ xin việc, PV…).
                            </li>
                            <li>
                              <b>Thông tin HĐ lao động</b>: nhập số liệu trên HĐ (lương, thời hạn…) và đánh dấu
                              HĐ hiệu lực — dùng khi tính lương. Không thay thế bước upload file.
                            </li>
                          </ol>
                          <p className="text-[11px] opacity-80">
                            Hai mục độc lập: upload file không tự tạo bản ghi lương; chọn HĐ bên dưới cũng
                            không gắn với file đã tải.
                          </p>
                        </UsageHint>

                        <Section
                          icon={FileText}
                          title="Tài liệu nhân sự"
                          subTitle="Lưu trữ file hồ sơ — không dùng để tính lương"
                        >
                          <UsageHint>
                            <p>
                              Upload nhiều file để lưu trữ (PDF/Word/ảnh…). Với hợp đồng lao động, chọn loại{" "}
                              <b>Hợp đồng lao động</b>. Đây chỉ là kho tài liệu — hệ thống{" "}
                              <b>không lấy lương từ file</b>.
                            </p>
                          </UsageHint>
                          <EmployeeHrDocumentsPanel
                            employeeId={employeeId}
                            canEdit={canSensitiveEdit}
                          />
                        </Section>

                        {canSensitiveEdit ? (
                          <Section
                            icon={Briefcase}
                            title="Thông tin HĐ lao động"
                            subTitle="Dữ liệu số trên HĐ — phục vụ tính lương"
                          >
                            <UsageHint>
                              <p>
                                Tạo / chọn bản ghi HĐ, nhập <b>số HĐ, loại, ngày, mức lương, phụ cấp</b>, rồi
                                bật <b>Hợp đồng đang có hiệu lực</b>. Phiếu lương lấy mức lương/phụ cấp từ bản
                                ghi này — không lấy từ file đã upload ở trên.
                              </p>
                            </UsageHint>
                            <div className="space-y-4">
                              <FloatLabel label="Danh sách hợp đồng">
                                <Select
                                  styles={customSelectStyles}
                                  options={contracts.map((c) => ({
                                    value: c.id,
                                    label: `${c.contract_no || "HĐ"} (${c.start_date || "—"})`,
                                  }))}
                                  value={
                                    selectedContractId
                                      ? {
                                          value: selectedContractId,
                                          label:
                                            contracts.find((x) => x.id === selectedContractId)
                                              ?.contract_no || "Hợp đồng hiện tại",
                                        }
                                      : null
                                  }
                                  onChange={(opt) => {
                                    const id = opt?.value || null;
                                    setSelectedContractId(id);
                                    if (!id) {
                                      setContractForm({ ...INITIAL_CONTRACT_FORM });
                                      return;
                                    }
                                    const found = contracts.find((x) => x.id === id);
                                    if (found) {
                                      setContractForm({
                                        contract_no: found.contract_no || "",
                                        contract_type: found.contract_type || "indefinite",
                                        start_date: found.start_date || "",
                                        end_date: found.end_date || "",
                                        position: found.position || "",
                                        salary: found.salary != null ? String(found.salary) : "",
                                        allowance:
                                          found.allowance != null ? String(found.allowance) : "",
                                        note: found.note || "",
                                        is_active: found.is_active ?? true,
                                      });
                                    }
                                  }}
                                  placeholder="Tạo mới hoặc chọn hợp đồng..."
                                  isClearable
                                  {...modalSelectPortalProps}
                                />
                              </FloatLabel>

                              <div className="grid grid-cols-2 gap-3">
                                <FloatLabel label="Số hợp đồng">
                                  <input
                                    name="contract_no"
                                    value={contractForm.contract_no}
                                    onChange={handleContractChange}
                                    className={INPUT_CLASS}
                                  />
                                </FloatLabel>
                                <FloatLabel label="Loại hợp đồng">
                                  <Select
                                    styles={customSelectStyles}
                                    options={CONTRACT_TYPE_OPTIONS}
                                    value={CONTRACT_TYPE_OPTIONS.find(
                                      (o) => o.value === contractForm.contract_type
                                    )}
                                    onChange={(opt) =>
                                      setContractForm((prev) => ({
                                        ...prev,
                                        contract_type: opt?.value || "",
                                      }))
                                    }
                                    {...modalSelectPortalProps}
                                  />
                                </FloatLabel>
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <FloatLabel label="Ngày hiệu lực">
                                  <input
                                    name="start_date"
                                    type="date"
                                    value={contractForm.start_date}
                                    onChange={handleContractChange}
                                    className={INPUT_CLASS}
                                  />
                                </FloatLabel>
                                <FloatLabel label="Ngày hết hạn">
                                  <input
                                    name="end_date"
                                    type="date"
                                    value={contractForm.end_date}
                                    onChange={handleContractChange}
                                    className={INPUT_CLASS}
                                  />
                                </FloatLabel>
                              </div>

                              <div className="grid grid-cols-2 gap-3">
                                <FloatLabel label="Mức lương (VNĐ)">
                                  <input
                                    name="salary"
                                    type="number"
                                    value={contractForm.salary}
                                    onChange={handleContractChange}
                                    className={`${INPUT_CLASS} text-red-600 font-bold`}
                                  />
                                </FloatLabel>
                                <FloatLabel label="Phụ cấp">
                                  <input
                                    name="allowance"
                                    type="number"
                                    value={contractForm.allowance}
                                    onChange={handleContractChange}
                                    className={INPUT_CLASS}
                                  />
                                </FloatLabel>
                              </div>

                              <label className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  name="is_active"
                                  checked={!!contractForm.is_active}
                                  onChange={handleContractChange}
                                  className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-4 h-4"
                                />
                                <div>
                                  <span className="text-sm font-bold text-gray-700 uppercase tracking-tight">
                                    Hợp đồng đang có hiệu lực
                                  </span>
                                  <p className="mt-0.5 text-[11px] font-medium normal-case tracking-normal text-gray-500">
                                    Đánh dấu bản ghi dùng làm căn cứ lương hiện tại khi tính lương.
                                  </p>
                                </div>
                              </label>
                            </div>
                          </Section>
                        ) : (
                          <div className="rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-xs text-amber-900">
                            Bạn chỉ xem được tài liệu. Cần quyền U để tải lên / sửa thông tin HĐ lao động.
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}

                {activeTab === "elearning" && employeeId ? (
                  <EmployeeDocumentAccessPanel employeeId={employeeId} />
                ) : null}
              </div>

              {/* FOOTER */}
              <div className="shrink-0 bg-white px-8 py-5 flex justify-end gap-4 border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.03)]">
                <button
                  type="button"
                  onClick={() => onClose(false)}
                  className="px-8 py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-50 border border-gray-200 transition-all uppercase text-xs tracking-widest"
                >
                  Huỷ bỏ
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-10 py-2.5 rounded-xl bg-red-600 text-white font-bold flex items-center gap-2 hover:bg-red-700 hover:shadow-lg hover:shadow-red-200 active:scale-95 transition-all disabled:opacity-60 uppercase text-xs tracking-widest"
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  {isSaving ? "Đang xử lý..." : "Lưu hồ sơ"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EmployeeFormModal;